/**
 * InfoPopover — a click-to-open, dismissible help popover.
 *
 * The house style for progressive disclosure: pages show the *minimum* upfront
 * (a short title + one-line subtitle) and tuck the longer "what is this / how do
 * I use it" copy behind a small "?" affordance. Clicking it reveals a compact
 * card with the explanation and optional "Learn more" deep-links into the
 * How-to / Docs pages.
 *
 * Reusable + consistent: used by `PageHeader` (via its `help` / `learnMore`
 * props) and can be dropped inline next to any label, tab, metric, or field.
 *
 * Accessibility: real <button> trigger with aria-haspopup/expanded; closes on
 * outside-click and Escape; the panel is a role="dialog" with a labelled close.
 */
import { HelpCircle, Info, X, ArrowRight } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";

export interface LearnMore {
  /** Route path, e.g. "/howto" or "/docs". */
  to: string;
  /** Optional in-page anchor, e.g. "pipelines" -> /howto#pipelines. */
  hash?: string;
  /** Link text. Defaults to "Learn more". */
  label?: string;
}

type Side = "bottom" | "bottom-right" | "top" | "left" | "right";

const SIDE_POS: Record<Side, string> = {
  bottom: "top-full left-0 mt-2",
  "bottom-right": "top-full right-0 mt-2",
  top: "bottom-full left-0 mb-2",
  left: "right-full top-0 mr-2",
  right: "left-full top-0 ml-2",
};

export function InfoPopover({
  title,
  children,
  learnMore,
  side = "bottom",
  icon = "help",
  label = "More information",
  className = "",
}: {
  title?: ReactNode;
  children?: ReactNode;
  learnMore?: LearnMore | LearnMore[];
  side?: Side;
  icon?: "help" | "info";
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const Icon = icon === "info" ? Info : HelpCircle;
  const links = !learnMore ? [] : Array.isArray(learnMore) ? learnMore : [learnMore];

  return (
    <span ref={ref} className={`relative inline-flex align-middle ${className}`}>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-muted transition-colors hover:border-accent hover:text-accent ${
          open ? "border-accent text-accent" : "border-border"
        }`}
      >
        <Icon size={12} strokeWidth={2.25} />
      </button>

      {open && (
        <div
          id={panelId}
          role="dialog"
          className={`absolute z-50 ${SIDE_POS[side]} w-80 max-w-[min(20rem,88vw)] cursor-default rounded-lg border border-border bg-panel p-3.5 text-left shadow-xl`}
        >
          <div className="flex items-start justify-between gap-3">
            {title && <div className="text-sm font-semibold leading-snug text-text">{title}</div>}
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="-mr-1 -mt-1 shrink-0 rounded p-0.5 text-muted hover:text-text"
            >
              <X size={14} />
            </button>
          </div>

          {children && (
            <div className="mt-1.5 space-y-2 text-xs leading-relaxed text-muted [&_code]:rounded [&_code]:bg-border/40 [&_code]:px-1 [&_code]:py-0.5 [&_strong]:text-text">
              {children}
            </div>
          )}

          {links.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-border/60 pt-2.5">
              {links.map((l, i) => (
                <Link
                  key={i}
                  to={l.to}
                  hash={l.hash}
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                >
                  {l.label ?? "Learn more"}
                  <ArrowRight size={12} />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </span>
  );
}
